/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'

import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router'
import router from '../routes.tsx';
import { ThemeProvider } from 'styled-components';
import { SoundManagerProvider } from './SoundContext';

import 'bootstrap/dist/css/bootstrap.min.css'
import '../fbot.css'

const darkTheme = {
    name: 'dark',
    colors: {
        bg: '#343a40',
        logBg: 'black',
        text: 'white',
        ctext: 'lime',
        textMuted: '#888',
        queueBg: 'black',
        mapBg: 'black',
        mapStroke: '#238f23',
        mapFill: '#303030',
        mapGrat: '#002000',
        progressBg: 'black',
        progressFg: 'lime',
        toastText: 'white',
        toastBg: '#212529',
        tableBg: '#343a40',
        headerBg: '#1a1e22',
        accordionBg: '#021e39',
        accordionActiveBg: '#004080',
        formControlDisabledBg: '#35004a',
        promptTextColor: '#0ff',
        responseTextColor: '#ff0',
        nodePending: '#ff0',
        nodeGood: 'lime',
        nodeBad: '#f00',
        nodeRoot: '#0ff',
        nodeRunning: '#f0f',
        jsonEditorBg: '#212529',
        jsonEditorColor: 'lime',
        // special colors that are not part of the theme but are
        // bootstrap colors
        primary: '#0d6efd',
        secondary: '#6c757d',
        success: '#198754',
        danger: '#dc3545',
        warning: '#ffc107',
        alreadyRunning: '#ffeb3b',
        downgrade: '#ff4d4f',
        info: '#0dcaf0',
        light: '#f8f9fa',
        dark: '#212529',
        grey: '#555',
    }
};

const selectCaretColor = darkTheme.colors.ctext || 'lime';
const selectCaretColorEncoded = selectCaretColor.startsWith('#')
    ? `%23${encodeURIComponent(selectCaretColor.slice(1))}`
    : encodeURIComponent(selectCaretColor);
const closeIconColorEncoded = selectCaretColorEncoded;

const root = ReactDOM.createRoot(document.getElementById('root'));
// Add global CSS overrides
const globalStyles = document.createElement('style');
globalStyles.innerHTML = `
    :root {
        --bs-body-bg: ${darkTheme.colors.bg};
        --bs-body-color: ${darkTheme.colors.text};
        --bs-primary: ${darkTheme.colors.primary};
        --bs-secondary: ${darkTheme.colors.secondary};
        --bs-success: ${darkTheme.colors.success};
        --bs-danger: ${darkTheme.colors.danger};
        --bs-warning: ${darkTheme.colors.warning};
        --bs-info: ${darkTheme.colors.info};
        --bs-light: ${darkTheme.colors.light};
        --bs-dark: ${darkTheme.colors.dark};
    }
    .accordion {
        --bs-accordion-bg: ${darkTheme.colors.accordionBg};
        --bs-accordion-active-bg: ${darkTheme.colors.accordionActiveBg};
        --bs-accordion-color: ${darkTheme.colors.ctext};
        --bs-accordion-active-color: ${darkTheme.colors.warning};
        --bs-accordion-active-bg: ${darkTheme.colors.bg};
        --bs-accordion-btn-color: ${darkTheme.colors.ctext};
        --bs-accordion-border-color: ${darkTheme.colors.ctext};
        --bs-accordion-border-width: 1px;
    }
    .table {
        --bs-table-color: ${darkTheme.colors.ctext};
        --bs-table-bg: ${darkTheme.colors.tableBg};
        --bs-table-border-color: ${darkTheme.colors.ctext};
        --bs-table-accent-bg: ${darkTheme.colors.tableBg};
        --bs-table-striped-color: ${darkTheme.colors.ctext};
        --bs-table-striped-bg: ${darkTheme.colors.tableBg};
        --bs-table-active-color: ${darkTheme.colors.ctext};
        --bs-table-active-bg: ${darkTheme.colors.tableBg};
    }
    .table-hover tbody tr:hover {
        --bs-table-hover-color: ${darkTheme.colors.info};
    }
    .nav-link, .nav-tabs .nav-link, .nav-tabs .nav-link:hover {
        --bs-nav-link-color: ${darkTheme.colors.ctext};
        --bs-nav-tabs-link-color: ${darkTheme.colors.ctext};
        --bs-nav-tabs-link-active-color: ${darkTheme.colors.ctext};
        --bs-nav-link-hover-color: ${darkTheme.colors.ctext};
    }
    .navbar-dark-dropdown .dropdown-menu {
        background-color: ${darkTheme.colors.dark};
        border-color: ${darkTheme.colors.dark};
    }
    .navbar-dark-dropdown .dropdown-item {
        color: ${darkTheme.colors.ctext};
    }
    .navbar-dark-dropdown .dropdown-item.active,
    .navbar-dark-dropdown .dropdown-item:active {
        background-color: ${darkTheme.colors.accordionActiveBg};
        color: ${darkTheme.colors.ctext};
    }
    .navbar-dark-dropdown .dropdown-divider,
    .dropdown-menu .dropdown-divider,
    .dropdown-divider {
        border-top-color: ${darkTheme.colors.ctext};
        opacity: 1;
    }
    .tooltip {
        --bs-tooltip-bg: ${darkTheme.colors.bg};
        --bs-tooltip-color: ${darkTheme.colors.ctext};
    }
    .popover {
        --bs-popover-bg: ${darkTheme.colors.logBg};
        --bs-popover-color: ${darkTheme.colors.ctext};
        --bs-popover-border-color: ${darkTheme.colors.ctext};
        --bs-popover-header-bg: ${darkTheme.colors.bg};
        --bs-popover-header-color: ${darkTheme.colors.ctext};
    }
    .card {
        --bs-card-bg: ${darkTheme.colors.bg};
        --bs-card-color: ${darkTheme.colors.ctext};
        --bs-card-border-color: ${darkTheme.colors.ctext};
    }
    .card-header {
        background-color: ${darkTheme.colors.headerBg};
        color: ${darkTheme.colors.ctext};
        border-bottom: 1px solid ${darkTheme.colors.ctext};
    }
    .text-muted {
        color: ${darkTheme.colors.textMuted} !important;
    }
    .form-control {
        background-color: ${darkTheme.colors.bg};
        color: ${darkTheme.colors.ctext};
        border: 1px solid ${darkTheme.colors.ctext};
    }
    .form-select {
        background-color: ${darkTheme.colors.bg};
        color: ${darkTheme.colors.ctext};
        border: 1px solid ${darkTheme.colors.ctext};
        box-shadow: none;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        --bs-form-select-bg-img: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='${selectCaretColorEncoded}' d='M2 0L0 2h4z'/%3E%3C/svg%3E");
        background-image: var(--bs-form-select-bg-img) !important;
        background-repeat: no-repeat;
        background-position: right .75rem center;
        background-size: 8px 10px;
        padding-right: 2rem;
    }
    .form-select::-ms-expand {
        display: none;
    }
    .form-select:focus {
        border-color: ${darkTheme.colors.ctext};
        box-shadow: 0 0 0 0.15rem ${darkTheme.colors.ctext};
    }
    .form-select option {
        background-color: ${darkTheme.colors.bg};
        color: ${darkTheme.colors.ctext};
    }
    .form-control:disabled, .form-control[readonly] {
        background-color: ${darkTheme.colors.formControlDisabledBg};
        opacity: 1;
    }
    .form-control::placeholder {
        color: ${darkTheme.colors.textMuted};
        opacity: 1;
    }
    .form-control::-ms-input-placeholder { /* Internet Explorer 10-11 */
        color: ${darkTheme.colors.textMuted};
    }
    .dropdown-item:hover, .dropdown-item:focus {
        background-color: ${darkTheme.colors.accordionActiveBg};
        color: ${darkTheme.colors.ctext};
    }
    .modal-xx1 {
        width: 1200px;
        max-width: 1200px;
    }
    .btn-close {
        opacity: 1;
        filter: none;
        --bs-btn-close-bg: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='${closeIconColorEncoded}'%3E%3Cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3E%3C/svg%3E");
    }
    .flist-table-wrap {
        max-height: 600px;
        overflow-y: auto;
    }
    .flist-table thead th {
        position: sticky;
        top: 0;
        background-color: ${darkTheme.colors.headerBg};
        z-index: 2;
    }
    .update-pill {
        display: inline-block;
        margin-left: 0.4rem;
        padding: 0.1rem 0.5rem;
        border-radius: 999px;
        font-size: 0.78rem;
        font-weight: 700;
        line-height: 1.2;
        border: 1px solid transparent;
    }
    .update-pill-release {
        color: ${darkTheme.colors.info};
        border-color: ${darkTheme.colors.info};
        background-color: rgba(13, 202, 240, 0.12);
    }
    .update-pill-already-running {
        color: ${darkTheme.colors.alreadyRunning};
        border-color: ${darkTheme.colors.alreadyRunning};
        background-color: rgba(255, 235, 59, 0.12);
    }
    .update-pill-downgrade {
        color: ${darkTheme.colors.downgrade};
        border-color: ${darkTheme.colors.downgrade};
        background-color: rgba(255, 77, 79, 0.12);
    }
`;
document.head.append(globalStyles, document.head.firstChild);
global.myWebsocket = null;
global.ws_waiters = [];

root.render(
        <ThemeProvider theme={darkTheme}>
            <SoundManagerProvider>
                <RouterProvider router={router}/>
            </SoundManagerProvider>
        </ThemeProvider>
);